import React, { useState } from 'react';
import { Book, FileText, PenTool, Calendar, CheckCircle, Download,  ClipboardList, GraduationCap, Lightbulb, Trophy, Clock, DollarSign, Brain, Globe, Calculator, Flask, Languages, Star, Award } from 'lucide-react';
import { FaGraduationCap, FaRupeeSign, FaUserGraduate } from 'react-icons/fa';

const NTSEScholarshipPage = () => {
  const [selectedStage, setSelectedStage] = useState('Stage1');

  const stageData = {
    'Stage1': {
      name: "Stage I - State Level Examination",
      fullName: "NTSE State Examination (SAT & MAT)",
      conductedBy: "State/UT Education Departments",
      icon: <FaGraduationCap size={30} />,
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      examPattern: {
        mat: { name: "MAT (Mental Ability Test)", marks: 100, questions: 100, duration: "90 Minutes" },
        sat: { name: "SAT (Scholastic Aptitude Test)", marks: 100, questions: 100, duration: "90 Minutes" },
        total: { marks: 200, duration: "3 Hours", date: "Usually November" }
      },
      eligibility: "Students studying in Class X in government/recognized schools with minimum 60% marks in Class IX (55% for SC/ST/PWD)"
    },
    'Stage2': {
      name: "Stage II - National Level Examination",
      fullName: "NTSE National Examination (Final Selection)",
      conductedBy: "NCERT (National Council of Educational Research and Training)",
      icon: <Trophy size={30} />,
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      examPattern: {
        mat: { name: "MAT (Mental Ability Test)", marks: 100, questions: 100, duration: "90 Minutes" },
        sat: { name: "SAT (Scholastic Aptitude Test)", marks: 100, questions: 100, duration: "90 Minutes" },
        total: { marks: 200, duration: "3 Hours", date: "Usually May-June" }
      },
      eligibility: "Only students who qualify Stage I examination are eligible for Stage II"
    }
  };

  const scholarshipBenefits = [
    { icon: <FaRupeeSign />, title: "Monthly Scholarship", desc: "₹1,250/month for Class XI & XII" },
    { icon: <GraduationCap />, title: "UG Level", desc: "₹2,000/month during graduation (3-4 years)" },
    { icon: <Trophy />, title: "PG Level", desc: "₹2,000/month during post-graduation (2 years)" },
    { icon: <Award />, title: "National Recognition", desc: "Prestigious award by Government of India" },
    { icon: <Star />, title: "Career Advantage", desc: "Adds significant value to academic profile" },
    { icon: <Book />, title: "No Restrictions", desc: "Study in any recognized institution in India" }
  ];

  const matSyllabus = {
    "Verbal Reasoning": [
      "Analogies: Word & Letter Analogies",
      "Classification: Odd Man Out",
      "Series Completion: Number, Letter & Mixed Series",
      "Coding-Decoding: Letter & Number Coding",
      "Blood Relations: Family Tree Problems",
      "Direction Sense Test",
      "Logical Venn Diagrams",
      "Alphabet Test & Ranking",
      "Mathematical Operations",
      "Puzzle Test",
      "Statement & Conclusions",
      "Statement & Assumptions"
    ],
    "Non-Verbal Reasoning": [
      "Pattern Completion",
      "Figure Matrix",
      "Paper Folding & Cutting",
      "Rule Detection",
      "Grouping of Figures",
      "Series Completion",
      "Analogy",
      "Classification",
      "Mirror Images",
      "Water Images",
      "Embedded Figures",
      "Dot Situation"
    ]
  };

  const satSyllabus = {
    "Mathematics": [
      "Number System: Rational & Irrational Numbers",
      "Polynomials: Factorization & Algebraic Identities",
      "Linear Equations in Two Variables",
      "Quadratic Equations",
      "Arithmetic Progressions",
      "Coordinate Geometry",
      "Triangles: Similarity & Congruence",
      "Circles: Tangents & Properties",
      "Constructions",
      "Trigonometry: Ratios & Identities",
      "Heights & Distances",
      "Mensuration: Areas & Volumes",
      "Statistics & Probability"
    ],
    "Science": [
      "Physics: Motion, Force, Work Energy Power, Gravitation",
      "Light: Reflection & Refraction",
      "Human Eye, Electricity, Magnetic Effects",
      "Chemistry: Matter & Its Classification, Atoms & Molecules",
      "Periodic Table, Chemical Reactions, Acids Bases Salts",
      "Metals & Non-metals, Carbon Compounds",
      "Biology: Life Processes, Control & Coordination",
      "Reproduction, Heredity & Evolution",
      "Our Environment, Natural Resources"
    ],
    "Social Science": [
      "History: Rise of Nationalism, Making of India",
      "Nationalism in Europe, Industrialization",
      "Geography: Resources, Agriculture, Industries",
      "Water, Minerals, Power Resources",
      "Civics: Power Sharing, Federalism, Democracy",
      "Political Parties, Social Divisions",
      "Economics: Development, Sectors of Economy",
      "Money & Credit, Globalization, Consumer Rights"
    ]
  };

  const importantDates = [
    { event: "NTSE Notification Release", date: "July - August", stage: "State Level" },
    { event: "Online Registration Opens", date: "August - September", stage: "State Level" },
    { event: "Last Date to Apply", date: "September - October", stage: "State Level" },
    { event: "Admit Card Release", date: "October - November", stage: "Stage I" },
    { event: "Stage I Examination", date: "November", stage: "State Level" },
    { event: "Stage I Result Declaration", date: "January - February", stage: "State Level" },
    { event: "Stage II Registration", date: "February - March", stage: "National Level" },
    { event: "Stage II Admit Card", date: "April - May", stage: "National Level" },
    { event: "Stage II Examination", date: "May - June", stage: "National Level" },
    { event: "Final Result Declaration", date: "September - October", stage: "National Level" }
  ];

  const preparationStrategy = [
    {
      title: "NCERT Mastery",
      desc: "Complete Class 9 & 10 NCERT thoroughly for SAT preparation",
      icon: <Book />
    },
    {
      title: "Daily Practice",
      desc: "Solve MAT questions daily to improve speed & accuracy",
      icon: <PenTool />
    },
    {
      title: "Previous Papers",
      desc: "Solve last 10 years papers of both stages multiple times",
      icon: <FileText />
    },
    {
      title: "Time Management",
      desc: "Practice 3-hour full-length mock tests regularly",
      icon: <Clock />
    },
    {
      title: "Current Affairs",
      desc: "Read newspapers & stay updated with national events",
      icon: <Globe />
    },
    {
      title: "Conceptual Clarity",
      desc: "Focus on understanding concepts rather than rote learning",
      icon: <Lightbulb />
    }
  ];

  const selectionProcess = [
    {
      stage: "Stage I",
      title: "State/UT Level Examination",
      desc: "Conducted by respective State/UT. Top students (quota-based) qualify for Stage II",
      percentage: "Top 3-5% students per state"
    },
    {
      stage: "Stage II",
      title: "National Level Examination",
      desc: "Conducted by NCERT across India. Final selection of 1,000 scholars",
      percentage: "1,000 scholarships nationwide"
    },
    {
      stage: "Final",
      title: "Scholarship Award",
      desc: "Selected students receive monthly scholarship till PhD completion",
      percentage: "Continues till PhD"
    }
  ];

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        padding: '80px 20px 60px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>
            <Trophy size={64} style={{ display: 'inline' }} />
          </div>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '20px' }}>
            NTSE Scholarship Program
          </h1>
          <p style={{ fontSize: '1.4rem', marginBottom: '15px', opacity: 0.95 }}>
            National Talent Search Examination
          </p>
          <p style={{ fontSize: '1.2rem', marginBottom: '30px', opacity: 0.9 }}>
            Premier Scholarship Program for Class 10 Students by NCERT
          </p>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '20px 25px',
            borderRadius: '10px',
            display: 'inline-block',
            marginBottom: '30px',
            maxWidth: '900px'
          }}>
            <p style={{ fontSize: '1.1rem', margin: 0, lineHeight: '1.6' }}>
              <strong>🎯 Scholarship Value:</strong> ₹1,250/month (Class XI-XII) → ₹2,000/month (Graduation & Post-Graduation) → ₹2,000/month (PhD)<br/>
              <strong>Total Support:</strong> Up to ₹3,00,000+ throughout academic journey
            </p>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => alert('Enrollment functionality')}
              style={{
                background: 'white',
                color: '#667eea',
                padding: '15px 40px',
                borderRadius: '30px',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Enroll for NTSE Preparation
            </button>
            <button
              onClick={() => document.getElementById('syllabus').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '15px 40px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}
            >
              View Complete Syllabus
            </button>
          </div>
        </div>
      </div>

      {/* Stage Selection Tabs */}
      <div style={{ 
        background: 'white', 
        padding: '30px 20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        position: 'relative',
        top: '0',
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {['Stage1', 'Stage2'].map(stage => (
              <button
                key={stage}
                onClick={() => setSelectedStage(stage)}
                style={{
                  padding: '25px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedStage === stage 
                    ? stageData[stage].gradient
                    : '#f0f0f0',
                  color: selectedStage === stage ? 'white' : '#333',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedStage === stage ? `0 4px 15px ${stageData[stage].color}40` : 'none',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (selectedStage !== stage) {
                    e.currentTarget.style.background = '#e0e0e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedStage !== stage) {
                    e.currentTarget.style.background = '#f0f0f0';
                  }
                }}
              >
                <div className='text-center mx-auto  display-3' style={{ marginBottom: '15px' }}>
                  {stageData[stage].icon}
                </div>
                <div className='text-center mx-auto' style={{ fontSize: '1.4rem', marginBottom: '8px' }}>
                  {stageData[stage].name}
                </div>
                <div className='text-center mx-auto' style={{ fontSize: '0.95rem', opacity: selectedStage === stage ? 0.9 : 0.7 }}>
                  {stageData[stage].conductedBy}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* Overview Section */}
        <section style={{ marginBottom: '60px' }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            border: `3px solid ${stageData[selectedStage].color}`
          }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ 
                fontSize: '3.5rem', 
                color: stageData[selectedStage].color,
                marginBottom: '20px'
              }}>
                {stageData[selectedStage].icon}
              </div>
              
              <h2 style={{ 
                fontSize: '2.5rem', 
                fontWeight: 'bold', 
                marginBottom: '10px',
                color: '#2c3e50'
              }}>
                {stageData[selectedStage].name}
              </h2>
              
              <p style={{ 
                fontSize: '1.2rem',
                color: '#666',
                marginBottom: '10px'
              }}>
                {stageData[selectedStage].fullName}
              </p>

              <p style={{ 
                fontSize: '1.1rem',
                color: '#888',
                fontStyle: 'italic'
              }}>
                Conducted by: {stageData[selectedStage].conductedBy}
              </p>
            </div>

            <div style={{
              padding: '25px',
              background: `${stageData[selectedStage].color}10`,
              borderRadius: '15px',
              border: `2px solid ${stageData[selectedStage].color}30`
            }}>
              <div style={{ fontSize: '2rem', color: stageData[selectedStage].color, marginBottom: '15px', textAlign: 'center' }}>
                <FaUserGraduate size={40} style={{ display: 'inline' }} />
              </div>
              <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '12px', color: '#2c3e50', textAlign: 'center' }}>
                Eligibility Criteria
              </h3>
              <p style={{ color: '#666', lineHeight: '1.8', margin: 0, fontSize: '1.05rem', textAlign: 'center' }}>
                {stageData[selectedStage].eligibility}
              </p>
            </div>
          </div>
        </section>

        {/* Scholarship Benefits */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            NTSE Scholarship Benefits
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {scholarshipBenefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '35px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${stageData[selectedStage].color}30`;
                e.currentTarget.style.borderColor = stageData[selectedStage].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'transparent';
              }}>
                <div style={{ 
                  fontSize: '3.5rem',
                  color: stageData[selectedStage].color,
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{ 
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50'
                }}>
                  {benefit.title}
                </h3>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.6',
                  margin: 0,
                  fontSize: '1rem'
                }}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Selection Process */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            NTSE Selection Process
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '25px'
            }}>
              {selectionProcess.map((process, idx) => (
                <div key={idx} style={{
                  padding: '30px',
                  background: idx === 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 
                              idx === 1 ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' :
                              'linear-gradient(135deg, #0fd752ff 0%, #00b593ff 100%)',
                  borderRadius: '15px',
                  color: 'white',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{ 
                    fontSize: '2.5rem',
                    marginBottom: '15px',
                    fontWeight: 'bold'
                  }}>
                    {idx + 1}
                  </div>
                  <h3 style={{ 
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {process.title}
                  </h3>
                  <p style={{ 
                    marginBottom: '15px',
                    opacity: 0.95,
                    lineHeight: '1.6'
                  }}>
                    {process.desc}
                  </p>
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}>
                    {process.percentage}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Exam Pattern */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <ClipboardList style={{ color: stageData[selectedStage].color }} />
            Exam Pattern - {stageData[selectedStage].name}
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '30px',
              marginBottom: '30px'
            }}>
              <div style={{
                padding: '30px',
                background: `${stageData[selectedStage].color}10`,
                borderRadius: '15px',
                border: `2px solid ${stageData[selectedStage].color}30`
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: stageData[selectedStage].color,
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Brain />
                  {stageData[selectedStage].examPattern.mat.name}
                </h3>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2c3e50' }}>
                    {stageData[selectedStage].examPattern.mat.marks} Marks
                  </div>
                  <div style={{ color: '#666', marginTop: '5px' }}>
                    {stageData[selectedStage].examPattern.mat.questions} Questions
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50' }}>
                    {stageData[selectedStage].examPattern.mat.duration}
                  </div>
                  <div style={{ color: '#666', marginTop: '5px' }}>Duration</div>
                </div>
              </div>

              <div style={{
                padding: '30px',
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                borderRadius: '15px',
                color: '#2c3e50'
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Book />
                  {stageData[selectedStage].examPattern.sat.name}
                </h3>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {stageData[selectedStage].examPattern.sat.marks} Marks
                  </div>
                  <div style={{ marginTop: '5px', opacity: 0.8 }}>
                    {stageData[selectedStage].examPattern.sat.questions} Questions
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {stageData[selectedStage].examPattern.sat.duration}
                  </div>
                  <div style={{ marginTop: '5px', opacity: 0.8 }}>Duration</div>
                </div>
              </div>
            </div>

            <div style={{
              padding: '25px',
              background: '#e3f2fd',
              borderRadius: '10px',
              border: '2px solid #2196f3',
              textAlign: 'center'
            }}>
              <h4 style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '15px', color: '#1565c0' }}>
                Total Exam Duration
              </h4>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#2c3e50', marginBottom: '10px' }}>
                {stageData[selectedStage].examPattern.total.duration}
              </div>
              <div style={{ fontSize: '1.2rem', color: '#666' }}>
                Total {stageData[selectedStage].examPattern.total.marks} Marks | Exam Date: {stageData[selectedStage].examPattern.total.date}
              </div>
              <div style={{ marginTop: '15px', padding: '15px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <p style={{ margin: 0, color: '#856404', fontWeight: '600' }}>
                  <strong>⚠️ Note:</strong> There is NO negative marking in NTSE examination
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Syllabus Section - MAT */}
        <section id="syllabus" style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Brain style={{ color: stageData[selectedStage].color }} />
            MAT (Mental Ability Test) Syllabus
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '25px'
          }}>
            {Object.entries(matSyllabus).map(([section, topics], idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '30px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                border: `2px solid ${stageData[selectedStage].color}20`,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = stageData[selectedStage].color;
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${stageData[selectedStage].color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${stageData[selectedStage].color}20`;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  marginBottom: '20px',
                  color: stageData[selectedStage].color,
                  borderBottom: `3px solid ${stageData[selectedStage].color}`,
                  paddingBottom: '10px'
                }}>
                  {section}
                </h3>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0,
                  margin: 0
                }}>
                  {topics.map((topic, i) => (
                    <li key={i} style={{ 
                      padding: '10px 0',
                      borderBottom: i < topics.length - 1 ? '1px solid #f0f0f0' : 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      color: '#555',
                      fontSize: '0.95rem'
                    }}>
                      <CheckCircle size={16} style={{ color: stageData[selectedStage].color, marginTop: '4px', flexShrink: 0 }} />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Syllabus Section - SAT */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Book style={{ color: stageData[selectedStage].color }} />
            SAT (Scholastic Aptitude Test) Syllabus
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '25px'
          }}>
            {Object.entries(satSyllabus).map(([subject, topics], idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: '30px',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                border: `2px solid ${stageData[selectedStage].color}20`,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = stageData[selectedStage].color;
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = `0 8px 25px ${stageData[selectedStage].color}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${stageData[selectedStage].color}20`;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  marginBottom: '20px',
                  color: stageData[selectedStage].color,
                  borderBottom: `3px solid ${stageData[selectedStage].color}`,
                  paddingBottom: '10px'
                }}>
                  {subject}
                </h3>
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0,
                  margin: 0
                }}>
                  {topics.map((topic, i) => (
                    <li key={i} style={{ 
                      padding: '10px 0',
                      borderBottom: i < topics.length - 1 ? '1px solid #f0f0f0' : 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      color: '#555',
                      fontSize: '0.95rem'
                    }}>
                      <CheckCircle size={16} style={{ color: stageData[selectedStage].color, marginTop: '4px', flexShrink: 0 }} />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '30px',
            padding: '25px',
            background: '#fff3cd',
            borderRadius: '15px',
            border: '2px solid #ffc107'
          }}>
            <h4 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '12px', color: '#856404' }}>
              📚 Important Note on SAT Syllabus
            </h4>
            <p style={{ margin: 0, color: '#856404', lineHeight: '1.8' }}>
              The SAT syllabus is primarily based on <strong>NCERT Class 9 and 10 textbooks</strong> covering Mathematics, Science (Physics, Chemistry, Biology), and Social Science (History, Geography, Civics, Economics). Focus on understanding concepts rather than memorization.
            </p>
          </div>
        </section>

        {/* Important Dates */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Calendar style={{ color: stageData[selectedStage].color }} />
            Important Dates 2024-25
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ overflow: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '1rem'
              }}>
                <thead>
                  <tr style={{ background: stageData[selectedStage].gradient, color: 'white' }}>
                    <th style={{ padding: '15px', textAlign: 'left', borderRadius: '8px 0 0 0' }}>Event</th>
                    <th style={{ padding: '15px', textAlign: 'center' }}>Timeline</th>
                    <th style={{ padding: '15px', textAlign: 'right', borderRadius: '0 8px 0 0' }}>Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {importantDates.map((item, idx) => (
                    <tr key={idx} style={{ 
                      background: idx % 2 === 0 ? '#f8f9fa' : 'white',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <td style={{ padding: '15px', fontWeight: '600', color: '#2c3e50' }}>
                        <Calendar size={16} style={{ display: 'inline', marginRight: '10px', color: stageData[selectedStage].color }} />
                        {item.event}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                        {item.date}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'right', color: '#666', fontSize: '0.9rem', fontWeight: '600' }}>
                        {item.stage}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Previous Year Papers */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <FileText style={{ color: stageData[selectedStage].color }} />
            Previous Year Question Papers
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {['2024', '2023', '2022', '2021', '2020', '2019'].map((year) => (
                <button key={year} style={{
                  padding: '25px',
                  borderRadius: '12px',
                  border: '2px solid #e8e8e8',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = stageData[selectedStage].gradient;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#2c3e50';
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <span>NTSE {year} - MAT & SAT</span>
                  <Download size={20} />
                </button>
              ))}
            </div>
            <p style={{ 
              textAlign: 'center', 
              marginTop: '30px',
              color: '#666',
              fontSize: '1rem'
            }}>
              Download previous year papers with detailed solutions for both stages
            </p>
          </div>
        </section>

        {/* Preparation Strategy */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <Lightbulb style={{ color: stageData[selectedStage].color }} />
            Winning Preparation Strategy
          </h2>

          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
          }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '25px'
            }}>
              {preparationStrategy.map((tip, idx) => (
                <div key={idx} style={{
                  padding: '25px',
                  border: '2px solid #e8e8e8',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = stageData[selectedStage].color;
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${stageData[selectedStage].color}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e8e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{ 
                    fontSize: '2.5rem',
                    color: stageData[selectedStage].color,
                    marginBottom: '15px',
                    display: 'flex'
                  }}>
                    {tip.icon}
                  </div>
                  <h4 style={{ 
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    marginBottom: '10px',
                    color: '#2c3e50'
                  }}>
                    {tip.title}
                  </h4>
                  <p style={{ 
                    color: '#666',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Course Packages */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            NTSE Preparation Courses
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                name: "Foundation Course",
                price: "6,999",
                duration: "4 Months",
                features: [
                  "Complete MAT & SAT Coverage",
                  "NCERT Based Study Material",
                  "20 Mock Tests (Both Stages)",
                  "Video Lectures",
                  "Weekly Doubt Sessions"
                ],
                gradient: stageData[selectedStage].gradient
              },
              {
                name: "Advanced Course",
                price: "11,999",
                duration: "6 Months",
                features: [
                  "Everything in Foundation",
                  "Live Interactive Classes",
                  "40+ Mock Tests + Analysis",
                  "Previous 10 Years Papers",
                  "State & National Level Prep",
                  "Personal Mentoring",
                  "Current Affairs Updates"
                ],
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                popular: true
              },
              {
                name: "Complete Package",
                price: "16,999",
                duration: "12 Months",
                features: [
                  "Everything in Advanced",
                  "One-on-One Mentoring",
                  "Unlimited Mock Tests",
                  "Interview Preparation",
                  "State-wise Focused Training",
                  "Performance Tracking",
                  "Study Planner & Resources",
                  "24/7 Premium Support"
                ],
                gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              }
            ].map((pkg, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)',
                border: pkg.popular ? '3px solid #667eea' : '1px solid #e8e8e8',
                position: 'relative',
                transform: pkg.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.08)' : 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 15px 45px rgba(102,126,234,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.05)' : 'scale(1)';
                e.currentTarget.style.boxShadow = pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '-35px',
                    background: '#FFD700',
                    color: '#333',
                    padding: '5px 40px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}>
                    BEST SELLER
                  </div>
                )}
                
                <div style={{
                  background: pkg.gradient,
                  padding: '40px 30px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <h3 style={{ 
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {pkg.name}
                  </h3>
                  <div style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '10px' }}>
                    ₹{pkg.price}
                  </div>
                  <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                    {pkg.duration}
                  </div>
                </div>

                <div style={{ padding: '30px' }}>
                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 25px 0'
                  }}>
                    {pkg.features.map((feature, i) => (
                      <li key={i} style={{
                        padding: '12px 0',
                        borderBottom: i < pkg.features.length - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#555'
                      }}>
                        <CheckCircle size={16} style={{ color: '#43e97b', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '10px',
                    border: 'none',
                    background: pkg.gradient,
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => alert('Enrollment functionality')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            {[
              {
                q: "What is NTSE and who conducts it?",
                a: "NTSE (National Talent Search Examination) is a national-level scholarship program conducted by NCERT for Class 10 students. It identifies and nurtures talented students by providing monthly scholarships from Class 11 to PhD level."
              },
              {
                q: "How much scholarship does NTSE provide?",
                a: "NTSE scholars receive ₹1,250/month in Class XI-XII, ₹2,000/month during graduation (3-4 years), ₹2,000/month during post-graduation (2 years), and ₹2,000/month during PhD. Total support exceeds ₹3,00,000."
              },
              {
                q: "What is the eligibility for NTSE?",
                a: "Students studying in Class X from any recognized school (Government/Private) with minimum 60% marks in Class IX (55% for SC/ST/PWD) are eligible. There is no age limit for NTSE."
              },
              {
                q: "How many stages are there in NTSE?",
                a: "NTSE has two stages: Stage I (State/UT Level) conducted by respective state education departments, and Stage II (National Level) conducted by NCERT for Stage I qualifiers."
              },
              {
                q: "Is there negative marking in NTSE?",
                a: "No, there is NO negative marking in NTSE examination. Students can attempt all questions without worrying about losing marks for wrong answers."
              },
              {
                q: "How many students are selected in NTSE?",
                a: "Approximately 1,000 students are selected nationwide every year from Stage II examination. Each state has a specific quota based on population."
              },
              {
                q: "Can I appear for NTSE if I'm in CBSE/ICSE/State Board?",
                a: "Yes, students from all boards (CBSE, ICSE, State Boards) can appear for NTSE. The exam is based on NCERT curriculum and is board-agnostic."
              },
              {
                q: "What happens if I qualify Stage I but fail in Class X boards?",
                a: "If you fail in Class X board exams, your NTSE Stage I qualification becomes invalid and you cannot appear for Stage II examination."
              },
              {
                q: "Can I get the scholarship while studying abroad?",
                a: "No, NTSE scholarship is valid only for students studying in recognized institutions within India. It cannot be availed for studies abroad."
              },
              {
                q: "What is the difference between MAT and SAT?",
                a: "MAT (Mental Ability Test) tests logical reasoning, verbal & non-verbal reasoning skills. SAT (Scholastic Aptitude Test) tests knowledge of Mathematics, Science, and Social Science based on NCERT Class 9-10 syllabus."
              }
            ].map((faq, idx) => (
              <div key={idx} style={{
                padding: '30px',
                borderBottom: idx < 9 ? '1px solid #e8e8e8' : 'none'
              }}>
                <h4 style={{ 
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: stageData[selectedStage].color,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px'
                }}>
                  <span style={{ flexShrink: 0 }}>Q{idx + 1}.</span>
                  <span>{faq.q}</span>
                </h4>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.8',
                  margin: '0 0 0 35px',
                  fontSize: '1.05rem'
                }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div style={{
            background: stageData[selectedStage].gradient,
            padding: '60px 40px',
            borderRadius: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <Trophy size={64} />
            </div>
            <h2 style={{ 
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              Ready to Achieve NTSE Success?
            </h2>
            <p style={{ 
              fontSize: '1.3rem',
              marginBottom: '35px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 35px'
            }}>
              Join India's most successful NTSE preparation program and secure your scholarship
            </p>
            <div style={{ 
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
               onClick={() => window.location.href = '/register'}
                style={{
                  background: 'white',
                  color: stageData[selectedStage].color,
                  padding: '18px 45px',
                  borderRadius: '30px',
                  border: 'none',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                }}>
                Start NTSE Preparation Now
              </button>
              <button style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '18px 45px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
             onClick={() => window.location.href = '/contact'}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = stageData[selectedStage].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}>
                Get Free Counselling
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default NTSEScholarshipPage;